import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, CircleNotch, Crown, CreditCard, Lightning, Coins, Warning,
  ArrowClockwise, CheckCircle, ArrowRight, Prohibit,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";

/**
 * Customer-facing subscription management. Shows current plan, credits,
 * recurring status, next renewal date, and cancel/resume actions.
 *
 * The wording adapts to whether the user is on a recurring subscription
 * (returned by the backend as `recurring: {...}` block on `/subscription/me`)
 * or a legacy prepaid grant (no `recurring` block, only `expires_at`).
 * Legacy users never see cancel controls — their access simply expires.
 */

const dt = (s) => (s ? new Date(s).toLocaleDateString(undefined, {
  year: "numeric", month: "long", day: "numeric",
}) : "—");
const money = (n, cur = "usd") => `$${(Number(n) || 0).toFixed(2)} ${(cur || "usd").toUpperCase()}`;
const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  return fb;
};

const STATUS_META = {
  active:   { label: "Active",           tone: "emerald" },
  trialing: { label: "Trialing",         tone: "blue"    },
  past_due: { label: "Past due",         tone: "amber"   },
  unpaid:   { label: "Unpaid",           tone: "red"     },
  canceled: { label: "Canceled",         tone: "zinc"    },
  incomplete: { label: "Incomplete",     tone: "amber"   },
  incomplete_expired: { label: "Expired", tone: "zinc"   },
  paused:   { label: "Paused",           tone: "amber"   },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || "Unknown", tone: "zinc" };
  const tones = {
    emerald: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
    blue:    "bg-blue-500/15 text-blue-300 ring-blue-400/30",
    amber:   "bg-amber-500/15 text-amber-300 ring-amber-400/30",
    red:     "bg-red-500/15 text-red-300 ring-red-400/30",
    zinc:    "bg-zinc-600/25 text-zinc-300 ring-zinc-400/20",
  };
  return (
    <span data-testid="sub-status-badge" className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full ring-1 ${tones[meta.tone]}`}>
      {meta.label}
    </span>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-white/10 bg-black/40 p-4">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
      <p className="font-display font-bold text-lg text-white truncate">{value}</p>
      {sub && <p className="text-[11px] text-zinc-500 truncate">{sub}</p>}
    </div>
  );
}

export default function AccountSubscription() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);  // "cancel" | "resume" | null

  const load = useCallback(async () => {
    try {
      const r = await api.get("/subscription/me");
      setInfo(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't load subscription."));
      setInfo({});
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (authLoading || !user) return null;

  const recurring = info?.recurring || null;
  const isRecurring = !!recurring?.stripe_subscription_id;
  const isCanceledScheduled = !!recurring?.cancel_at_period_end;
  const stripeStatus = recurring?.status;
  const legacyExpiresAt = info?.expires_at;
  const isAdmin = !!info?.is_admin;
  const isLegacyPro = !isRecurring && !isAdmin && info?.is_pro;
  const isFree = !info?.is_pro && !info?.is_admin;

  // Resolve plan pricing from the tiers/plans dictionary served by /subscription/me.
  const plansMap = info?.plans || {};
  const planId = info?.plan_id || recurring?.stripe_subscription_id ? info?.plan_id : null;
  const planMeta = planId && plansMap[planId];
  const renewalAmount = planMeta?.amount;
  const renewalCurrency = planMeta?.currency;

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Cancellation scheduled. Your access remains active until the current period ends.");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't cancel subscription."));
    } finally { setBusy(false); }
  };
  const doResume = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed — it will renew as scheduled.");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't resume subscription."));
    } finally { setBusy(false); }
  };

  const monthlyRemaining = Math.max(0, (info?.ai_limit || 0) - (info?.ai_used || 0));
  const walletBalance = Number(info?.topup_credits || 0);

  return (
    <div data-testid="account-subscription" className="min-h-screen bg-[#070707] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} data-testid="account-back-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors">
            <ArrowLeft size={18} />
          </button>
          <CreditCard size={20} weight="fill" className="text-amber-300" />
          <span className="font-display font-extrabold text-lg tracking-tight">Subscription &amp; Credits</span>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-8 space-y-6">
        {info === null ? (
          <p data-testid="sub-loading" className="text-sm text-zinc-500 py-16 text-center">
            <CircleNotch size={16} className="inline animate-spin mr-2" /> Loading…
          </p>
        ) : (
          <>
            {/* Plan headline card */}
            <section data-testid="sub-plan-card" className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-2">
                    Current plan
                    {isRecurring && <span data-testid="sub-recurring-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 ring-1 ring-amber-400/30 text-[10px]"><ArrowClockwise size={10} weight="bold" /> Recurring</span>}
                    {isLegacyPro && <span data-testid="sub-legacy-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-700/40 text-zinc-300 ring-1 ring-white/10 text-[10px]">Prepaid</span>}
                    {stripeStatus && <StatusBadge status={stripeStatus} />}
                  </div>
                  <p className="font-display font-black text-3xl tracking-tight">
                    {isAdmin ? "Admin (Unlimited)"
                      : info.is_pro ? (planMeta?.name || (info.tier ? `${info.tier[0].toUpperCase() + info.tier.slice(1)} Pro` : "Pro"))
                      : "Free"}
                  </p>
                  {info.is_pro && (
                    <p className="text-sm text-zinc-400 mt-1">
                      {isRecurring ? (
                        isCanceledScheduled
                          ? <>Access ends on <span className="text-white font-semibold">{dt(recurring.current_period_end)}</span></>
                          : recurring.interval === "year"
                            ? "Automatically renews annually"
                            : "Automatically renews monthly"
                      ) : isLegacyPro ? (
                        <>Prepaid access — one-time purchase. Expires <span className="text-white font-semibold">{dt(legacyExpiresAt)}</span></>
                      ) : isAdmin ? "You have unlimited access as an administrator." : null}
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {isFree && (
                    <button data-testid="sub-upgrade-btn" onClick={() => navigate("/pricing")}
                      className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-amber-400 text-black hover:bg-amber-300 text-sm font-bold transition-colors">
                      <Crown size={14} weight="fill" /> Upgrade to Pro <ArrowRight size={14} weight="bold" />
                    </button>
                  )}
                  {isLegacyPro && (
                    <button data-testid="sub-switch-to-recurring-btn" onClick={() => navigate("/pricing")}
                      className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-white text-black hover:bg-zinc-100 text-sm font-bold transition-colors">
                      <ArrowClockwise size={14} weight="bold" /> Switch to auto-renew
                    </button>
                  )}
                  {isRecurring && !isCanceledScheduled && stripeStatus === "active" && (
                    <button data-testid="sub-cancel-btn" onClick={() => setConfirm("cancel")}
                      className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-white/15 hover:border-red-500/40 hover:text-red-300 text-sm font-semibold transition-colors">
                      <Prohibit size={13} weight="bold" /> Cancel subscription
                    </button>
                  )}
                  {isRecurring && isCanceledScheduled && (
                    <button data-testid="sub-resume-btn" onClick={() => setConfirm("resume")}
                      className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-amber-400 text-black hover:bg-amber-300 text-sm font-bold transition-colors">
                      <ArrowClockwise size={13} weight="bold" /> Resume subscription
                    </button>
                  )}
                </div>
              </div>

              {/* Recurring detail strip */}
              {isRecurring && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat testid="sub-interval"
                    label="Billing"
                    value={recurring.interval === "year" ? "Annual" : "Monthly"}
                    sub="Auto-renewing" />
                  <Stat testid="sub-price"
                    label={isCanceledScheduled ? "Was renewing at" : "Renewal price"}
                    value={renewalAmount ? money(renewalAmount, renewalCurrency) : "—"}
                    sub={renewalAmount ? `Charged every ${recurring.interval === "year" ? "year" : "month"}` : ""} />
                  <Stat testid="sub-next-renewal"
                    label={isCanceledScheduled ? "Access ends on" : "Next renewal"}
                    value={dt(recurring.current_period_end)}
                    sub={recurring.current_period_start ? `From ${dt(recurring.current_period_start)}` : ""} />
                  <Stat testid="sub-cancellation"
                    label="Cancellation"
                    value={isCanceledScheduled ? "Scheduled" : "Not scheduled"}
                    sub={isCanceledScheduled ? "Auto-renew off" : "Renews automatically"} />
                </div>
              )}

              {isLegacyPro && (
                <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-zinc-400">
                  You purchased a one-time prepaid Pro grant. You will <span className="text-white font-semibold">not</span> be charged again.
                  When your access expires you&apos;ll return to the Free tier — your workspace, saved projects, brand systems, and top-up credits stay intact.
                </div>
              )}
            </section>

            {/* Credits card */}
            <section data-testid="sub-credits-card" className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex items-center gap-2 mb-4">
                <Lightning size={16} weight="fill" className="text-amber-300" />
                <p className="font-display font-black text-lg tracking-tight">Credits</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Stat testid="sub-monthly-credits"
                  label={isRecurring
                    ? (recurring.interval === "year" ? "Annual bucket" : "This month")
                    : (isLegacyPro ? "This month" : "Free credits")}
                  value={monthlyRemaining}
                  sub={info.ai_limit ? `of ${info.ai_limit} allowed` : (isFree ? "one-time free grant" : "—")} />
                <Stat testid="sub-topup-credits"
                  label="Top-up wallet"
                  value={walletBalance}
                  sub="Never expires" />
                <Stat testid="sub-total-remaining"
                  label="Total remaining"
                  value={info.ai_remaining}
                  sub="Across every source" />
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs">
                <p className="text-zinc-400">
                  {isRecurring ? (
                    <>Credits refill according to your plan. {recurring.interval === "year"
                      ? "Your full annual bucket was deposited at purchase."
                      : "Your monthly bucket refills on the renewal date above."}</>
                  ) : isLegacyPro ? (
                    <>Your prepaid credits do not refill after expiration. Buy a top-up pack to stock up.</>
                  ) : (
                    <>Free tier gets a one-time grant. Top-up packs are the fastest way to add more.</>
                  )}
                </p>
                <button data-testid="sub-buy-topup-btn" onClick={() => navigate("/pricing")}
                  className="ml-auto inline-flex items-center gap-1 h-8 px-3 rounded-full border border-white/15 hover:bg-white/5 text-xs font-semibold text-zinc-200 whitespace-nowrap">
                  <Coins size={12} weight="fill" /> Buy top-up
                </button>
              </div>
            </section>

            {/* Info footer */}
            <section className="text-[11px] text-zinc-500 leading-relaxed">
              {isRecurring ? (
                <p>Cancel before your next renewal to avoid the next charge. Your saved projects, workspace, brand systems, palettes, and top-up credits are always preserved regardless of subscription status.</p>
              ) : (
                <p>Your saved projects, workspace, brand systems, palettes, and any top-up credits belong to you and never expire.</p>
              )}
            </section>
          </>
        )}
      </main>

      {/* Confirm dialogs */}
      {confirm === "cancel" && (
        <Dialog open onOpenChange={(o) => !o && setConfirm(null)}>
          <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="cancel-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg inline-flex items-center gap-2">
                <Warning size={18} weight="fill" className="text-amber-300" /> Cancel your subscription?
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-zinc-300">
              <p>Your access stays active until <span className="text-white font-bold">{dt(recurring?.current_period_end)}</span>. You won&apos;t be charged again after that.</p>
              <ul className="text-xs text-zinc-400 space-y-1 pl-4 list-disc">
                <li>Your saved projects, brand systems, and workspace data are kept.</li>
                <li>Your top-up credits stay in your wallet — they never expire.</li>
                <li>You can resume any time before <span className="text-zinc-200 font-semibold">{dt(recurring?.current_period_end)}</span>.</li>
              </ul>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirm(null)} className="text-zinc-400 hover:text-white hover:bg-white/10">Keep subscription</Button>
              <Button data-testid="cancel-confirm-btn" onClick={doCancel} disabled={busy}
                className="bg-red-500/90 text-white hover:bg-red-500 font-bold disabled:opacity-50">
                {busy ? <><CircleNotch size={14} className="animate-spin mr-1" /> Canceling…</> : "Yes, cancel"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {confirm === "resume" && (
        <Dialog open onOpenChange={(o) => !o && setConfirm(null)}>
          <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="resume-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-lg inline-flex items-center gap-2">
                <CheckCircle size={18} weight="fill" className="text-emerald-400" /> Resume your subscription?
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm text-zinc-300">
              <p>Your subscription will continue renewing automatically. Next renewal is scheduled for <span className="text-white font-bold">{dt(recurring?.current_period_end)}</span>.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirm(null)} className="text-zinc-400 hover:text-white hover:bg-white/10">Not now</Button>
              <Button data-testid="resume-confirm-btn" onClick={doResume} disabled={busy}
                className="bg-amber-400 text-black hover:bg-amber-300 font-bold disabled:opacity-50">
                {busy ? <><CircleNotch size={14} className="animate-spin mr-1" /> Resuming…</> : "Yes, resume"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
